import { DateTime, Settings } from 'luxon';

export class LuxonNomoh {
  static readonly TIMEZONE_NEGOCIO = 'America/Sao_Paulo';

  /**
   * Configurações globais do Luxon para o projeto Nomoh.
   * Define o fuso horário padrão e a localidade.
   */
  static configureLuxon(): void {
    Settings.defaultZone = 'local'; // Use the user's local timezone
    Settings.defaultLocale = 'pt-BR'; // Localidade padrão para português do Brasil
  }

  /**
   * Retorna a data e hora atual no fuso horário padrão do projeto (local).
   * @returns Um objeto DateTime representando a data e hora de hoje.
   */
  static hoje(): DateTime {
    return DateTime.now();
  }

  static convertToUtc(date: Date | string): DateTime {
    let dateTime: DateTime;

    if (typeof date === 'string') {
      // Se a string já tem timezone (Z ou +/-offset), interpreta corretamente
      // Caso contrário, assume que é horário local de São Paulo
      if (date.includes('Z') || date.match(/[+-]\d{2}:\d{2}$/)) {
        dateTime = DateTime.fromISO(date, { locale: 'pt-BR' });
      } else {
        dateTime = DateTime.fromISO(date, {
          zone: 'America/Sao_Paulo',
          locale: 'pt-BR',
        });
      }
    } else if (date instanceof Date) {
      // Para objetos Date, interpreta como UTC (padro do banco)
      dateTime = DateTime.fromJSDate(date, { zone: 'utc' });
      dateTime = dateTime.setLocale('pt-BR');
    } else {
      // Caso seja um DateTime do Luxon já pronto
      dateTime = (date as unknown as DateTime).setZone('utc').setLocale('pt-BR');
    }

    return dateTime;
  }
  static convertToLocal(date: Date | string | DateTime): DateTime {
  let dateTime: DateTime;

  if (typeof date === 'string') {
    dateTime = DateTime.fromISO(date, { locale: 'pt-BR' });
  } else if (date instanceof Date) {
    dateTime = DateTime.fromJSDate(date);
    dateTime.setLocale('pt-BR');
  } else {
    // date is already a DateTime
    dateTime = date.setLocale('pt-BR');
  }

  return dateTime.setZone('local');
}

  static dateToPgTime(value: Date): string {
    return this.convertToUtc(value).toFormat('HH:mm:ss');
  }

  // Função para início do dia
  static startOfDay(date: Date | string | DateTime): DateTime {
    return this.convertToLocal(date).startOf('day');
  }

  // Função para fim do dia
  static endOfDay(date: Date | string | DateTime): DateTime {
  return this.convertToLocal(date).endOf('day');
}

static dateTimeNow(): DateTime {
  return DateTime.now().setLocale('pt-BR');
}

static formatarDataHora(data: Date | string | DateTime, format: string) {
  const dateTime = this.convertToLocal(data);
  return dateTime.toFormat(format ?? 'dd/MM/yyyy - HH:mm');
}

  static formatarDataHistorico(
    data: Date | string | DateTime | null | undefined,
    format = 'dd/MM/yyyy HH:mm'
  ): string {
    if (data == null || data === '') {
      return '';
    }

    let dateTime: DateTime;

    if (data instanceof DateTime) {
      dateTime = data;
    } else if (data instanceof Date) {
      dateTime = DateTime.fromJSDate(data);
    } else if (typeof data === 'string') {
      const normalizada = data.trim().includes('T')
        ? data.trim()
        : data.trim().replace(' ', 'T');
      const temOffset =
        normalizada.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(normalizada);
      dateTime = DateTime.fromISO(
        temOffset ? normalizada : `${normalizada}Z`,
        { zone: 'utc' }
      );
    } else {
      return '';
    }

    if (!dateTime.isValid) {
      return '';
    }

    return dateTime.setZone('local').toFormat(format);
  }

  static inicioDoDiaSaoPaulo(): Date {
    return DateTime.now()
      .setZone(LuxonNomoh.TIMEZONE_NEGOCIO)
      .startOf('day')
      .toJSDate();
  }

  static estaVencidoPorDataCivil(
    dataFim: Date | string | null | undefined
  ): boolean {
    if (dataFim == null || dataFim === '') {
      return false;
    }

    return this.dataCivilSaoPaulo(dataFim) < this.hojeCivilSaoPaulo();
  }

  static diasRestantesDataCivil(dataFim: Date | string): number {
    return Math.round(
      this.dataCivilSaoPaulo(dataFim).diff(this.hojeCivilSaoPaulo(), 'days')
        .days
    );
  }

  private static hojeCivilSaoPaulo(): DateTime {
    return DateTime.now().setZone(LuxonNomoh.TIMEZONE_NEGOCIO).startOf('day');
  }

  private static dataCivilSaoPaulo(data: Date | string): DateTime {
    if (typeof data === 'string') {
      const normalizada = data.trim().includes('T')
        ? data.trim()
        : data.trim().replace(' ', 'T');
      const dt = DateTime.fromISO(normalizada, { setZone: true });
      if (dt.isValid) {
        return dt.setZone(LuxonNomoh.TIMEZONE_NEGOCIO).startOf('day');
      }
    }

    return DateTime.fromJSDate(
      data instanceof Date ? data : new Date(data)
    )
      .setZone(LuxonNomoh.TIMEZONE_NEGOCIO)
      .startOf('day');
  }

}
